import React, { useCallback } from "react";
import { fromLocalInputValue } from "./lib/time";
import useProject from "./hooks/useProject";
import useEvents from "./hooks/useEvents";
import useInlineEdit from "./hooks/useInlineEdit";
import LogoUploader from "./components/LogoUploader";
import ProjectForm from "./components/ProjectForm";
import ProjectSummary from "./components/ProjectSummary";
import TimelineChart from "./components/TimelineChart";
import EventForm from "./components/EventForm";
import EventTable from "./components/EventTable";
import ErrorBanner from "./components/ErrorBanner";
import DprEditor from "./components/DprEditor";

const Timeline = ({ projectId, userId, onProjectUpdated }) => {
  const {
    project, loading, saving, error: projectError,
    handleProjectChange: rawProjectChange, handleSaveProject, handleLogoUpload,
  } = useProject(projectId, userId, onProjectUpdated);

  const {
    events, setEvents, newEvent, setNewEvent,
    totals, estimatedCompletion,
    error: eventsError,
    handleAddEvent, handleDeleteEvent, recomputeAndPersist,
  } = useEvents(projectId, userId, project.startAt);

  const {
    editingCell, draftValue, setDraftValue,
    error: editError,
    handleStartEdit, handleCommitEdit, handleCancelEdit,
  } = useInlineEdit(events, setEvents, project.startAt, projectId, userId);

  const error = projectError || eventsError || editError;

  const handleProjectChange = useCallback((field, value) => {
    if (field === "startAt") {
      rawProjectChange("startAt", fromLocalInputValue(value));
      return;
    }
    rawProjectChange(field, value);
  }, [rawProjectChange]);

  const handleSave = useCallback(async () => {
    const updated = await handleSaveProject();
    if (updated) {
      await recomputeAndPersist(updated.startAt);
    }
  }, [handleSaveProject, recomputeAndPersist]);

  const handleNewEventChange = useCallback((field, value) => {
    setNewEvent((prev) => ({ ...prev, [field]: value }));
  }, [setNewEvent]);

  if (!projectId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-8 text-sm text-zinc-500">
        Select a project, or create a new one.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="px-8 py-10 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
        Loading project…
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-8 py-10 xl:max-w-7xl">
      <ErrorBanner error={error} />

      <header className="mb-10 flex items-start justify-between gap-6">
        <div>
          <span className="section-eyebrow">Rig move</span>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">
            {project.title || "Untitled project"}
          </h1>
          {project.customer && (
            <p className="mt-1 text-sm text-zinc-500">{project.customer}</p>
          )}
        </div>
        <LogoUploader
          logoUrl={project.logoUrl}
          onUpload={handleLogoUpload}
          disabled={!projectId}
        />
      </header>

      <ProjectForm
        project={project}
        onChange={handleProjectChange}
        onSave={handleSave}
        saving={saving}
        disabled={!projectId}
      />

      <hr className="section-divider" />

      <ProjectSummary
        estimatedDays={totals.estimatedDays}
        actualDays={totals.actualDays}
        estimatedCompletion={estimatedCompletion}
      />

      <hr className="section-divider" />

      <section>
        <h2 className="section-eyebrow mb-4">Timeline</h2>
        <TimelineChart events={events} title={project.title} />
      </section>

      <hr className="section-divider" />

      <EventForm
        newEvent={newEvent}
        onChange={handleNewEventChange}
        onAdd={handleAddEvent}
        disabled={!projectId}
      />

      <hr className="section-divider" />

      <EventTable
        events={events}
        editingCell={editingCell}
        draftValue={draftValue}
        onStartEdit={handleStartEdit}
        onDraftChange={setDraftValue}
        onCommitEdit={handleCommitEdit}
        onCancelEdit={handleCancelEdit}
        onDeleteEvent={handleDeleteEvent}
      />

      <hr className="section-divider" />

      <DprEditor
        projectId={projectId}
        userId={userId}
        project={project}
        events={events}
      />
    </div>
  );
};

export default Timeline;
